import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, type MaybeRefOrGetter, toValue } from "vue";
import { type RouteLocationNormalizedLoaded, useRouter } from "vue-router";
import { HttpError, request } from "@/shared/http/client";
import { SUPPORT_KEY } from "@/shared/support-tab";

/**
 * OS WORKSPACES DA CONTA, do lado do painel.
 *
 * Uma conta é uma MATRIZ e os workspaces que ela criou. Cada workspace é um
 * tenant inteiro — isolado, com a equipe dele —, e tudo o que se paga cai na
 * matriz: plano, módulos, limites somados e a bolsa de créditos, 100%
 * partilhada. Criar um workspace não custa nada e não depende de plano.
 *
 * `GET /v3/organization` responde os três estados, e a tela não deduz nenhum:
 *
 *   - `role: null`   — um workspace sozinho: ainda não criou outro;
 *   - `role: "hq"`   — a matriz: `workspaces` traz todos, a matriz primeiro;
 *   - `role: "unit"` — um workspace da rede: `hqName` diz de quem, e
 *                      `structureOffer` diz se a oferta de copiar a estrutura
 *                      da matriz ainda está de pé.
 */

export interface WorkspaceView {
	readonly tenantId: string;
	readonly name: string;
	readonly slug: string;
	readonly status: string;
	readonly role: "hq" | "unit";
	readonly people: number;
	/** Créditos gastos por este workspace desde o início do ciclo da matriz. */
	readonly spentCredits: number;
}

export type StructureOffer = "pending" | "applied" | "dismissed";

export interface OrganizationView {
	readonly role: "hq" | "unit" | null;
	readonly hqName: string | null;
	/** Quantos workspaces a conta pode ter, a matriz incluída. 0 = sem limite. */
	readonly maxWorkspaces: number;
	readonly cycleStart: string | null;
	readonly workspaces: readonly WorkspaceView[];
	readonly totalSpentCredits: number;
	readonly structureOffer: StructureOffer | null;
}

export const organizationKey = ["organization"] as const;

export function useOrganization(enabled: MaybeRefOrGetter<boolean> = true) {
	return useQuery({
		queryKey: organizationKey,
		queryFn: () => request<OrganizationView>("/v3/organization"),
		enabled: () => toValue(enabled),
		// Curto: a lista mostra CONSUMO do ciclo, e um número de gasto com cinco
		// minutos de idade é o número que faz alguém duvidar dele.
		staleTime: 30_000,
	});
}

/**
 * ESTE workspace é cobrado pela matriz? — a pergunta do menu e das telas de
 * cobrança. Enquanto a leitura voa, `false`: esconder a Cobrança de quem paga
 * por um instante é pior do que mostrá-la um instante a quem não paga.
 */
export function useBilledByHq(enabled: MaybeRefOrGetter<boolean> = true) {
	const { data } = useOrganization(enabled);
	return {
		billedByHq: computed(() => data.value?.role === "unit"),
		hqName: computed(() => data.value?.hqName ?? ""),
	};
}

/** Toda escrita invalida a mesma chave — e o seletor, que lista os mesmos nomes. */
function useOrganizationMutation<TVars, TData>(fn: (vars: TVars) => Promise<TData>) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: fn,
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: organizationKey });
			void client.invalidateQueries({ queryKey: switcherKey });
		},
	});
}

export interface StructureOutcome {
	readonly applied: boolean;
	readonly reason: string | null;
	/** Regras que ficaram de fora: citam um canal que o destino não tem. */
	readonly leftOut: readonly string[];
	/** Chaves do cofre que as funções copiadas pedem e a unidade não tem — só o nome viaja. */
	readonly missingSecrets?: readonly string[];
}

export function useCreateWorkspace() {
	return useOrganizationMutation((body: { name: string; copyStructure: boolean }) =>
		request<{
			workspace: { tenantId: string; name: string; slug: string };
			structure: StructureOutcome | null;
		}>("/v3/organization/workspaces", {
			method: "POST",
			body,
			// Nascer um workspace é schema + agente + canal + (talvez) a cópia da
			// estrutura inteira: passa dos 15 s do padrão com folga.
			timeoutMs: 120_000,
		}),
	);
}

/**
 * Os workspaces de OUTRA conta, pela plataforma (dono, 27/10): a mesma vista e a
 * mesma criação da aba "Organização", pedidas por quem não mora no tenant.
 */
export function usePlatformOrganization(tenantId: MaybeRefOrGetter<string>) {
	return useQuery({
		queryKey: computed(() => ["platform-organization", toValue(tenantId)] as const),
		queryFn: () => request<OrganizationView>(`/v3/platform/organization/${toValue(tenantId)}`),
		staleTime: 30_000,
	});
}

export function useCreatePlatformWorkspace(tenantId: MaybeRefOrGetter<string>) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (body: { name: string; copyStructure: boolean }) =>
			request<{
				workspace: { tenantId: string; name: string; slug: string };
				structure: StructureOutcome | null;
			}>(`/v3/platform/organization/${toValue(tenantId)}/workspaces`, {
				method: "POST",
				body,
				timeoutMs: 120_000,
			}),
		onSuccess: () =>
			client.invalidateQueries({ queryKey: ["platform-organization", toValue(tenantId)] }),
	});
}

export function useArchiveWorkspace() {
	return useOrganizationMutation((tenantId: string) =>
		request<{ archived: boolean }>(`/v3/organization/workspaces/${tenantId}`, {
			method: "DELETE",
		}),
	);
}

/** A estrutura da MATRIZ aplicada nos workspaces escolhidos — do lado da matriz. */
export function useApplyStructureToWorkspaces() {
	return useOrganizationMutation((body: { unitTenantIds: string[] }) =>
		request<{ outcomes: Array<StructureOutcome & { tenantId: string }> }>(
			"/v3/organization/workspaces/structure",
			{ method: "POST", body, timeoutMs: 300_000 },
		),
	);
}

/** A oferta, de DENTRO do workspace: aplicar a estrutura da matriz aqui, ou dispensar. */
export function useStructureOffer() {
	const apply = useOrganizationMutation(() =>
		request<StructureOutcome>("/v3/organization/structure/apply", {
			method: "POST",
			timeoutMs: 300_000,
		}),
	);
	const dismiss = useOrganizationMutation(() =>
		request<{ dismissed: boolean }>("/v3/organization/structure/dismiss", { method: "POST" }),
	);
	return { apply, dismiss };
}

// ── O seletor ───────────────────────────────────────────────────────────────

export interface SwitcherEntry {
	readonly tenantId: string;
	readonly name: string;
	readonly logoFileId: string | null;
	readonly role: "hq" | "unit";
}

export interface SwitcherView {
	readonly current: string;
	readonly workspaces: readonly SwitcherEntry[];
}

export const switcherKey = ["organization", "switcher"] as const;

/** Onde ESTA pessoa pode entrar. Uma entrada só = nada a trocar. */
export function useSwitcher() {
	return useQuery({
		queryKey: switcherKey,
		queryFn: () => request<SwitcherView>("/v3/organization/switcher"),
		staleTime: 60_000,
	});
}

/**
 * TROCAR DE WORKSPACE.
 *
 * O servidor põe o cookie de renovação do workspace de destino; recarregar a
 * página inteira faz o arranque restaurar a sessão JÁ nele. É de propósito uma
 * recarga e não uma troca de token em memória: caches, streams (a fila, as
 * notificações) e a identidade são todos do workspace, e recomeçar do zero é a
 * única maneira de nenhum deles ficar a falar com o anterior.
 */
export function useSwitchWorkspace() {
	// Lido na montagem (setup), porque `useRouter` é injeção; sem router (um teste
	// que monta a tela solta) a troca cai em "/", como sempre caiu.
	const router = useRouter() as ReturnType<typeof useRouter> | undefined;
	return useMutation({
		mutationFn: (tenantId: string) =>
			request<{ accessToken: string; expiresInSeconds: number }>(
				`/v3/organization/switcher/${tenantId}`,
				{ method: "POST" },
			),
		onSuccess: () => {
			// Trocar de unidade é sair do modo suporte da aba: o link guardado faria a
			// recarga voltar ao cliente espiado em vez da unidade escolhida.
			sessionStorage.removeItem(SUPPORT_KEY);
			window.location.assign(landingAfterSwitch(router?.currentRoute.value));
		},
	});
}

/** Um uuid num valor de query é um registro DESTE workspace (`?turno=<id>`, `?open=<id>`). */
const RECORD_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * ONDE A TROCA ATERRA — na mesma tela, quando ela existe do outro lado.
 *
 * Trocar em `/channels` e cair em `/` obrigava a refazer o caminho a cada troca,
 * e quem compara duas unidades troca muitas vezes. Mas um endereço com PARÂMETRO
 * de rota (`/inbox/:id`, `/platform/:id`, `/billing/invoices/:month`) aponta um
 * registro deste workspace, que no outro não existe ou é outro — esses caem em
 * `/`. Da query fica o que não é id: `?aba=canais` viaja, `?turno=<uuid>` não.
 */
export function landingAfterSwitch(route: RouteLocationNormalizedLoaded | undefined): string {
	if (!route || route.matched.length === 0) return "/";
	const hasParam = Object.values(route.params).some((v) =>
		Array.isArray(v) ? v.length > 0 : v !== undefined && v !== "",
	);
	if (hasParam) return "/";
	const query = new URLSearchParams();
	for (const [key, value] of Object.entries(route.query)) {
		for (const v of Array.isArray(value) ? value : [value]) {
			if (typeof v === "string" && !RECORD_ID.test(v)) query.append(key, v);
		}
	}
	const search = query.toString();
	return search ? `${route.path}?${search}` : route.path;
}

/**
 * O CÓDIGO da recusa, para a tela escolher a frase.
 *
 * O servidor responde `{ error: "WORKSPACE_LIMIT" }` e uma mensagem em inglês
 * que ninguém desenha — a mesma disciplina do `billingErrorCode`. Sem código
 * reconhecido, `null`, e quem chama cai na frase genérica.
 */
export function organizationErrorCode(error: unknown): string | null {
	if (!(error instanceof HttpError)) return null;
	const payload = error.payload as { error?: unknown } | undefined;
	return typeof payload?.error === "string" ? payload.error : null;
}

/** Quais workspaces da rede uma pessoa da matriz vê (0316). */
export interface PersonWorkspacesView {
	readonly role: string;
	/** `false` = segue o papel (admin: todos; membro: só a matriz). */
	readonly chosen: boolean;
	readonly workspaces: ReadonlyArray<{ tenantId: string; name: string; reaches: boolean }>;
}

const personWorkspacesKey = (userId: string) => ["organization", "person", userId] as const;

export function usePersonWorkspaces(
	userId: MaybeRefOrGetter<string>,
	enabled: MaybeRefOrGetter<boolean>,
) {
	return useQuery({
		queryKey: computed(() => personWorkspacesKey(toValue(userId))),
		queryFn: () =>
			request<PersonWorkspacesView>(`/v3/organization/people/${toValue(userId)}/workspaces`),
		enabled: () => toValue(enabled),
	});
}

/** `null` devolve a pessoa ao papel. */
export function useSetPersonWorkspaces() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (vars: { userId: string; workspaces: string[] | null }) =>
			request<PersonWorkspacesView>(`/v3/organization/people/${vars.userId}/workspaces`, {
				method: "PUT",
				body: { workspaces: vars.workspaces },
			}),
		onSuccess: (view, vars) => client.setQueryData(personWorkspacesKey(vars.userId), view),
	});
}
